import { useEffect, useState } from 'react';
import { getApiUrl } from './apiConfig';
import { authHeaders } from './role';
import { useAuth } from '../context/AuthContext';

// The caller's analysable asset universe — SERVER SSOT (GET /api/tickers/universe), the same object the API
// asset-scope gate enforces. Product Eligibility Gate (owner 2026-09-28): a client-visible asset is SAA Qualified
// (identity + confirmed class + ratified framework + canonical Quality + ≥75% scoreable coverage + admissible
// load-bearing evidence + no P0). Identity-verified assets that have not cleared the gates are candidates
// ("qualification in progress") — never analysable, never counted. Until the server answers, the free five.
export interface AssetUniverse {
  plan: string;
  all: boolean;
  symbols: string[];
  rule: string;
  pending: string[]; // inside the candidate window, data identity not yet verified — not analysable
  candidates: string[]; // identity verified, qualification in progress — not analysable, not counted
  qualified: string[]; // SAA Qualified tier of the ratified universe version
  // Universe tier 2 (owner rulings OR-UNI-20261004-01..05): "Analysable · partial coverage" — analysable on Pro and
  // Institutional, composite withheld with the missing axes disclosed. symbols = qualified + analysable.
  analysable: string[];
  tiers: Record<string, AssetTierInfo>; // ratified tier + canonical coverage per accessible member
  qualifiedRun: string;
  // Universe policy exclusions in force (owner 2026-10-07, identity-keyed, e.g. WBTC): a held asset stays in the
  // book; it is labelled "excluded by universe policy" instead of being replaced.
  policyExclusions: PolicyExclusion[];
  ready: boolean; // the server answered — until then the free five are a placeholder, not a decision
}

export type UniverseTier = 'QUALIFIED' | 'ANALYSABLE';
export interface PolicyExclusion { asset_id: string; symbol: string; reason_code: string; reason: string; decision_ref: string; effective_from: string }

// Server code for a request on a policy-excluded asset (409; a methodological state, not a plan limit).
export const EXCLUDED_BY_UNIVERSE_POLICY = 'EXCLUDED_BY_UNIVERSE_POLICY';

// The message for an asset-scope refusal (409 / 402 body): a policy exclusion states the policy reason (never
// "qualification in progress"); other codes keep the server's text.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function assetScopeMessage(body: any, fallback: string): string {
  if (body?.code === EXCLUDED_BY_UNIVERSE_POLICY && body.reason) {
    const sym = String(body.symbol || body.asset_id || '').toUpperCase();
    return sym ? `${sym} — ${body.reason}` : String(body.reason);
  }
  return body?.error || fallback;
}

// The policy exclusion that applies to an asset given by canonical id and/or ticker (identity-keyed; no name rule).
export function policyExclusionFor(u: AssetUniverse, assetId?: string, sym?: string): PolicyExclusion | null {
  const id = (assetId || '').trim().toLowerCase();
  const s = (sym || '').trim().toLowerCase();
  return (u.policyExclusions || []).find((p) => (id && p.asset_id === id) || (s && p.symbol === s)) || null;
}
export interface AssetTierInfo { tier: UniverseTier; coverage?: number; acos?: number }

export const FREE_UNIVERSE: AssetUniverse = { plan: 'free', all: false, symbols: ['btc', 'eth', 'sol', 'xrp', 'ltc'], rule: 'BTC, ETH, SOL, XRP, LTC', pending: [], candidates: [], qualified: [], analysable: [], tiers: {}, qualifiedRun: '', policyExclusions: [], ready: false };

// The ratified tier of an asset in the caller's universe view (null when it is not a member / not accessible).
export function assetTier(u: AssetUniverse, sym: string): AssetTierInfo | null {
  const s = (sym || '').trim().toLowerCase();
  if (!s) return null;
  if (u.tiers[s]) return u.tiers[s];
  if (u.qualified.includes(s)) return { tier: 'QUALIFIED' };
  if (u.analysable.includes(s)) return { tier: 'ANALYSABLE' };
  return null;
}

// "Partial coverage · 53%" — the coverage is the canonical scoreable coverage of the ratified source run.
export function partialCoverageLabel(coverage?: number): string {
  return typeof coverage === 'number' && Number.isFinite(coverage) ? `Partial coverage · ${Math.round(coverage * 100)}%` : 'Partial coverage';
}

// The plan's asset number, split by tier when the plan reaches the second tier.
export function universeCountLabel(u: AssetUniverse): string {
  const a = u.analysable.length;
  const q = u.symbols.length - a;
  return a > 0 ? `${u.symbols.length} assets (${q} SAA Qualified + ${a} partial coverage)` : `${u.symbols.length} SAA Qualified assets`;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function parseTiers(raw: any): Record<string, AssetTierInfo> {
  const out: Record<string, AssetTierInfo> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [k, v] of Object.entries(raw as Record<string, any>)) {
    if (v && (v.tier === 'QUALIFIED' || v.tier === 'ANALYSABLE')) {
      out[k.toLowerCase()] = { tier: v.tier, coverage: typeof v.canonical_coverage === 'number' ? v.canonical_coverage : undefined, acos: typeof v.acos === 'number' ? v.acos : undefined };
    }
  }
  return out;
}

export function parsePolicyExclusions(raw: any): PolicyExclusion[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((p) => p && typeof p.asset_id === 'string').map((p) => ({ asset_id: p.asset_id.toLowerCase(), symbol: String(p.symbol || '').toLowerCase(),
    reason_code: String(p.reason_code || ''), reason: String(p.reason || ''), decision_ref: String(p.decision_ref || ''), effective_from: String(p.effective_from || '') }));
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function universeAllows(u: AssetUniverse, sym: string): boolean {
  return u.all || u.symbols.includes((sym || '').trim().toLowerCase());
}

export function useAssetUniverse(): AssetUniverse {
  const { tier, loading } = useAuth();
  const [u, setU] = useState<AssetUniverse>(FREE_UNIVERSE);
  useEffect(() => {
    if (loading) return;
    let dead = false;
    fetch(getApiUrl('/api/tickers/universe'), { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!dead && j?.success && j.data) {
          setU({ plan: j.data.plan, all: !!j.data.all, symbols: (j.data.symbols || []).map((s: string) => s.toLowerCase()), rule: j.data.rule || '',
            pending: (j.data.pending_identity || []).map((s: string) => s.toLowerCase()),
            candidates: (j.data.qualification_in_progress || []).map((s: string) => s.toLowerCase()),
            qualified: (j.data.saa_qualified || []).map((s: string) => s.toLowerCase()),
            analysable: (j.data.saa_analysable || []).map((s: string) => s.toLowerCase()), tiers: parseTiers(j.data.tiers),
            qualifiedRun: j.data.saa_qualified_run || '', policyExclusions: parsePolicyExclusions(j.data.policy_exclusions), ready: true });
        }
      })
      .catch(() => { if (!dead) setU({ ...FREE_UNIVERSE, ready: true }); /* keep the free universe */ });
    return () => { dead = true; };
  }, [tier, loading]);
  return u;
}

// Per-plan live counts (GET /api/tickers/universe-summary, public) for plan cards and the Analysis page.
export interface UniverseSummary {
  standard?: number; pro?: number; institutional?: number; proCandidates?: number; proPending?: number;
  // Second tier (OR-UNI-20261004): analysable · partial coverage counts per plan (Standard is always 0).
  proAnalysable?: number; institutionalAnalysable?: number;
  // Load state of the summary itself: a count is shown only when 'ok' (never a 0 or a stale number on error).
  status?: 'loading' | 'ok' | 'error';
  run?: string; // the ratified universe version the counts come from (saa_qualified_run)
  checkedAt?: string; // ISO time this page received the counts
}

export function useUniverseSummary(): UniverseSummary {
  const [s, setS] = useState<UniverseSummary>({ status: 'loading' });
  useEffect(() => {
    let dead = false;
    fetch(getApiUrl('/api/tickers/universe-summary'))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (dead) return;
        if (j?.success && j.data) setS({ standard: j.data.standard?.saa_qualified, pro: j.data.pro?.saa_qualified,
          institutional: j.data.institutional?.saa_qualified,
          proAnalysable: j.data.pro?.analysable ?? 0, institutionalAnalysable: j.data.institutional?.analysable ?? 0,
          proCandidates: j.data.pro?.qualification_in_progress, proPending: j.data.pro?.pending_identity,
          status: 'ok', run: j.data.saa_qualified_run || undefined, checkedAt: new Date().toISOString() });
        else setS({ status: 'error' });
      })
      .catch(() => { if (!dead) setS({ status: 'error' }); /* counts stay hidden, the error is labelled */ });
    return () => { dead = true; };
  }, []);
  return s;
}
